import type { QueryClientConfig } from '@tanstack/react-query'
import { QueryClient } from '@tanstack/react-query'
import type { AxiosInstance, Method } from 'axios'
import axios, { type AxiosRequestConfig } from 'axios'
import { camelCase } from 'change-case/keys'
import merge from 'deepmerge'
import type { ErrorObject } from 'serialize-error'
import { serializeError } from 'serialize-error'

declare module 'axios' {
  interface AxiosRequestConfig {
    _retry?: boolean
    _sponsorSwitch?: boolean
  }
}

import { Consts } from '@/configs/consts'
import type * as T from '@/services/api/api.types'
import type { ProcessProgress, UpdateContentTextResponse } from '@/services/api/api.types'

export class FileProcessingError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'FileProcessingError'
  }
}

export class Api {
  private httpClient: AxiosInstance
  private authHttpClient: AxiosInstance
  private tokenRefresher: (() => Promise<string | null>) | null = null
  private _sponsorSwitchBarrier: { promise: Promise<void>; resolve: () => void } | null = null
  queryClient: QueryClient

  constructor(axiosConfig: AxiosRequestConfig & { authToken?: string } = {}, tanstackConfig: QueryClientConfig = {}) {
    // Main client for non-auth endpoints - baseURL will be set dynamically after auth
    // Since VITE_SERVER_BASE_URL is no longer provided, this will be undefined initially
    this.httpClient = axios.create(
      merge(
        {
          baseURL: undefined, // Set dynamically after auth from ConfigStore
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true, // Send cookies with all requests
        },
        axiosConfig,
      ),
    )

    // Separate client for auth endpoints
    this.authHttpClient = axios.create(
      merge(
        {
          baseURL: Consts.authBaseUrl,
          headers: { 'Content-Type': 'application/json' },
        },
        axiosConfig,
      ),
    )

    this.setup401Interceptor()
    this.setupSponsorSwitchBarrier()

    if (axiosConfig.authToken) {
      this.setAuthToken(axiosConfig.authToken)
    }

    if (axiosConfig.baseURL) {
      this.setBaseUrl(axiosConfig.baseURL)
    }

    this.queryClient = new QueryClient(tanstackConfig)
  }

  /**
   * Register a callback that silently refreshes the auth token.
   * Called by AuthenticationStore after the store tree is assembled to avoid circular deps.
   */
  setTokenRefresher(fn: () => Promise<string | null>) {
    this.tokenRefresher = fn
  }

  /**
   * Intercept 401 responses on httpClient and attempt a silent token refresh + retry.
   * Only on httpClient -- authHttpClient is used for the refresh call itself,
   * so there's no risk of an infinite catch-loop.
   */
  private setup401Interceptor() {
    this.httpClient.interceptors.response.use(
      response => response,
      async (error: any) => {
        const originalRequest = error.config
        if (error.response?.status !== 401 || originalRequest?._retry) {
          return Promise.reject(error)
        }
        originalRequest._retry = true

        // Deduplication is handled inside refreshTokenSilently()
        const newToken = await (this.tokenRefresher?.() ?? Promise.resolve(null))
        if (!newToken) return Promise.reject(error)

        originalRequest.headers['Authorization'] = `Bearer ${newToken}`
        return this.httpClient.request(originalRequest)
      },
    )
  }

  /**
   * Block all httpClient requests (except the sponsor-switch itself) while a
   * sponsor switch is in flight.  This prevents concurrent requests from
   * receiving a default-sponsor Set-Cookie that overwrites the correct one.
   */
  private setupSponsorSwitchBarrier() {
    this.httpClient.interceptors.request.use(async config => {
      if (this._sponsorSwitchBarrier && !config._sponsorSwitch) {
        await this._sponsorSwitchBarrier.promise
      }
      return config
    })
  }

  beginSponsorSwitch() {
    this._sponsorSwitchBarrier?.resolve()

    let resolve!: () => void
    const promise = new Promise<void>(r => {
      resolve = r
    })
    this._sponsorSwitchBarrier = { promise, resolve }
  }

  endSponsorSwitch() {
    this._sponsorSwitchBarrier?.resolve()
    this._sponsorSwitchBarrier = null
  }

  setAuthToken(token: string | null) {
    const authHeader = !!token ? `Bearer ${token}` : null
    // Set auth header for all non-auth API endpoints (protected resources)
    this.httpClient.defaults.headers.common['Authorization'] = authHeader
    // Note: Auth endpoints selectively add auth headers only when needed (refresh, logout, etc.)
  }

  setBaseUrl(url: string | undefined) {
    this.httpClient.defaults.baseURL = url
  }

  get baseUrl() {
    return this.httpClient.defaults.baseURL
  }

  get authHeader(): string | null {
    return (this.httpClient.defaults.headers.common['Authorization'] as string) ?? null
  }

  setAuthBaseUrl(url: string) {
    this.authHttpClient.defaults.baseURL = url
  }

  async request<ResponseT>(
    path: string,
    method: Method,
    data: any = undefined,
    config?: AxiosRequestConfig & { camelcase?: boolean },
  ): Promise<ResponseT> {
    // TODO: Consider implementing snake_case transformation for outbound requests
    // This would allow frontend to use camelCase consistently while server expects snake_case
    // Would require: import { snakeCase } from 'change-case/keys' and transform request data
    const { camelcase = true, ...axiosConfig } = config ?? {}

    const response = await this.httpClient.request<ResponseT>({
      url: path,
      method,
      data: method !== 'get' ? data : undefined,
      params: method === 'get' ? data : undefined,
      ...axiosConfig,
    })

    if (camelcase) {
      return camelCase(response.data, 999) as ResponseT
    } else {
      return response.data as ResponseT
    }
  }

  async authRequest<ResponseT>(
    path: string,
    method: Method,
    data: any = undefined,
    config?: AxiosRequestConfig & { camelcase?: boolean },
  ): Promise<ResponseT> {
    // TODO: Consider implementing snake_case transformation for outbound requests
    // This would allow frontend to use camelCase consistently while server expects snake_case
    // Would require: import { snakeCase } from 'change-case/keys' and transform request data
    const { camelcase = true, ...axiosConfig } = config ?? {}

    const response = await this.authHttpClient.request<ResponseT>({
      url: path,
      method,
      data: method !== 'get' ? data : undefined,
      params: method === 'get' ? data : undefined,
      ...axiosConfig,
    })

    if (camelcase) {
      return camelCase(response.data, 999) as ResponseT
    } else {
      return response.data as ResponseT
    }
  }

  async authRequestWithAuth<ResponseT>(
    path: string,
    method: Method,
    data: any,
    config?: AxiosRequestConfig & { camelcase?: boolean },
  ): Promise<ResponseT> {
    // TODO: Consider implementing snake_case transformation for outbound requests
    // This would allow frontend to use camelCase consistently while server expects snake_case
    // Would require: import { snakeCase } from 'change-case/keys' and transform request data
    const { camelcase = true, ...axiosConfig } = config ?? {}

    // Add auth header for endpoints that require authentication
    const authHeader = this.httpClient.defaults.headers.common['Authorization']

    const response = await this.authHttpClient.request<ResponseT>({
      url: path,
      method,
      data: data,
      headers: {
        ...axiosConfig.headers,
        Authorization: authHeader,
      },
      ...axiosConfig,
    })

    if (camelcase) {
      return camelCase(response.data, 999) as ResponseT
    } else {
      return response.data as ResponseT
    }
  }

  async safeRequest<RequestT extends (...args: any[]) => Promise<any>, ErrorDataT = { message?: string }>(
    requestFn: RequestT,
    ...args: Parameters<RequestT>
  ): Promise<T.SafeRequestResponse<Awaited<ReturnType<RequestT>>, ErrorDataT>> {
    try {
      const response = await requestFn(...args)

      return { ok: true as const, data: response }
    } catch (error) {
      const e = error as Error & { response?: { data: ErrorDataT } }

      let parsedError: ErrorObject | undefined

      if (e instanceof Error) {
        parsedError = serializeError(e)
      }

      // Handle cases where response is unavailable (could be network, logic, or configuration errors)
      const errorData =
        e.response?.data ||
        ({
          message: e.message || 'Request failed with no error message or response available',
        } as ErrorDataT)

      const isNetworkError = axios.isAxiosError(e) && e.code === 'ERR_NETWORK'
      return { ok: false as const, data: errorData, error: parsedError, isNetworkError }
    }
  }

  getRuntimeConfig = async () => {
    return this.request<T.ConfigResponse>(`${window.location.origin}/config.json`, 'get', undefined, {
      camelcase: false,
    })
  }

  logout = async (params: T.LogoutParams) => {
    // Logout goes to main API server with auth header to invalidate session
    return this.request('/logout', 'post', params)
  }

  notifyLogin = async (params: T.NotifyLoginParams) => {
    return this.request<T.NotifyLoginResponse>('/notify-login', 'post', params)
  }

  /**
   * Authentication
   */
  login = async (params: T.LoginParams) => {
    // No auth header needed - getting initial token
    return this.authRequest<T.LoginResponse>('/login', 'post', params)
  }

  forgot = async (params: T.ForgotParams) => {
    // Auth header needed - changing password with old password
    return this.authRequestWithAuth<T.ForgotResponse>('/forgot', 'post', params)
  }

  forgotPassword = async (params: T.ForgotPasswordParams) => {
    // No auth header needed - initiate password reset
    return this.authRequest<T.ForgotPasswordResponse>('/forgot-password', 'post', params)
  }

  confirmForgotPassword = async (params: T.ConfirmForgotPasswordParams): Promise<T.ConfirmForgotPasswordResult> => {
    try {
      // No auth header needed - using confirmation code
      const response = await this.authHttpClient.post<T.ConfirmForgotPasswordResponse>('/confirm-forgot-password', {
        email: params.email,
        confirmation_code: params.code,
        new_password: params.newPassword,
      })

      const message = response.data?.detail
      if (!message) {
        return { kind: 'bad-data', data: response.data }
      }

      return { kind: 'ok', message }
    } catch (error: any) {
      return { kind: 'unknown', error, data: error.response?.data }
    }
  }

  refreshToken = async (params: T.RefreshTokenParams) => {
    // Auth header needed - refreshing existing token
    return this.authRequestWithAuth<T.LoginResponse>('/refresh', 'post', params)
  }

  checkUser = async (): Promise<T.CheckUserResponse> => {
    const response = await this.request<T.CheckUserResponse>('/check_user', 'get')

    // HACK: Stag and Itrl environments return a different api response. This is a temporary fix to handle the different responses.
    if ('message' in response) {
      return {
        access: { sme: response.message === 'success', app: true },
        isSponsor: false,
        isEditor: false,
        sponsorId: 'unknown-uuid',
        sponsorName: 'unknown-sponsor',
        userId: 'unknown-uuid',
        email: 'unknown',
      }
    }

    return response
  }

  /**
   * File Uploads & Downloads
   */
  uploadPrepare = async (params: T.PrepareUploadParams, signal?: AbortSignal) => {
    return this.request<T.PrepareUploadResponse>('/prepare-upload', 'get', params, {
      signal,
    })
  }

  upload = async (params: T.UploadParams, signal?: AbortSignal, onUploadProgress?: (percent: number) => void) => {
    return this.request(`/files/${params.file_path}`, 'put', params.file_content, {
      headers: { 'Content-Type': params.file_content.type },
      withCredentials: false,
      signal,
      onUploadProgress: onUploadProgress
        ? progressEvent => {
            if (progressEvent.total) {
              onUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total))
            }
          }
        : undefined,
    })
  }

  download = async (params: T.DownloadParams) => {
    const path = params.file_path.startsWith('files/') ? params.file_path : `files/${params.file_path}`
    return this.request(path, 'get', undefined, {
      withCredentials: false, // Disable credentials for S3 downloads to avoid CORS issues
    })
  }

  /**
   * Get content of an S3 file after it was rendered with the placeholders.
   * @param params
   * @returns
   */
  downloadRendered = async (params: T.DownloadRenderedParams): Promise<T.DownloadRenderedResponse> => {
    return this.request(`/content/get_rendered_file`, 'get', params)
  }

  /**
   * Content Modules
   */
  getContentModules = async (params: T.GetContentModulesParams = {}) => {
    const requestParams = { exclude_removed: true, ...params }
    return this.request<T.GetContentModulesResponse>('/cm/list', 'get', requestParams)
  }

  getContentModule = async (params: T.GetContentModuleParams) => {
    return this.request<T.GetContentModuleResponse>(`/cm/${params.task_id}`, 'get')
  }

  createContentModule = async (params: T.CreateContentModuleParams) => {
    const response = await this.request<T.CreateContentModuleResponse>('/cm/create', 'post', params, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })

    // If there's no processing needed, return immediately
    if (!response.inProcess) {
      return response
    }

    await this.pollForProcessing({
      getStatusFn: () => this.getContent({ task_id: response.taskId }),
      logPrefix: 'createContentModule',
    })

    return response
  }

  updateContentModule = async (params: T.UpdateContentModuleParams) => {
    const { lock, ...body } = params
    // Only attach the query string when the caller explicitly opts out. The
    // backend defaults to lock=true; sending it on every call would be noise.
    const config = lock === false ? { params: { lock: false } } : undefined
    return this.request<T.UpdateContentModuleResponse>('/cm/update', 'post', body, config)
  }

  changeContentModuleStatus = async (params: T.ChangeContentModuleStatusParams) => {
    return this.request<T.ChangeContentModuleStatusResponse>(`/cm/change_status`, 'post', params)
  }

  updateAdditionalOwners = async (params: T.UpdateAdditionalOwnersParams) => {
    return this.request<T.UpdateAdditionalOwnersResponse>(`/cm/${params.task_id}/additional_owners`, 'put', {
      additional_owners: params.additional_owners,
    })
  }

  lockContentModule = async (params: T.LockContentModuleParams) => {
    return this.request<void>('/cm/lock', 'post', { task_id: params.task_id })
  }

  unlockContentModule = async (params: T.UnlockContentModuleParams) => {
    return this.request<void>('/cm/unlock', 'post', {
      task_id: params.task_id,
      force_unlock: params.force_unlock,
    })
  }

  getSponsorInfo = async (): Promise<T.GetSponsorInfoResponse | null> => {
    return this.request<T.GetSponsorInfoResponse>('/sponsor/current', 'get')
  }

  getSponsorList = async (): Promise<T.GetSponsorListResponse> => {
    return this.request<T.GetSponsorListResponse>('/sponsor/sponsor-list', 'get')
  }

  resetSponsorContext = async (): Promise<{
    message: string
    previous_sponsor_id: string
  }> => {
    return this.request<{
      message: string
      previous_sponsor_id: string
    }>('/sponsor/sponsor-reset', 'post')
  }

  switchSponsor = async (
    sponsorId: string,
  ): Promise<{
    message: string
    previous_sponsor: {
      sponsor_id: string
      sponsor_name: string
      environment_name: string
      environment_url: string
    }
    new_sponsor: {
      sponsor_id: string
      sponsor_name: string
      environment_name: string
      environment_url: string
    }
  }> => {
    return this.request<{
      message: string
      previous_sponsor: {
        sponsor_id: string
        sponsor_name: string
        environment_name: string
        environment_url: string
      }
      new_sponsor: {
        sponsor_id: string
        sponsor_name: string
        environment_name: string
        environment_url: string
      }
    }>(
      '/sponsor/sponsor-switch',
      'post',
      { sponsor_id: sponsorId },
      {
        _sponsorSwitch: true,
      },
    )
  }

  /**
   * Disables a sponsor (channel) and deactivates all supported resources
   * associated with it. Server-side this is a soft delete, but from the
   * caller's perspective it is permanent and irreversible.
   *
   * After a successful call the caller should log the user out, since the
   * channel context their session is bound to no longer exists.
   */
  deleteSponsor = async (sponsorId: string): Promise<T.DeleteSponsorResponse> => {
    return this.request<T.DeleteSponsorResponse>(`/sponsor/delete/${encodeURIComponent(sponsorId)}`, 'delete')
  }

  /**
   * MSKs & ESKs
   */
  getESKs = async () => {
    const data = await this.request<T.GetESKsResponse>('/cm/available', 'get')

    // HACK: Dev environment returns duplicate ESKs. This is a temporary fix to dedupe the array.
    const newData = data.filter((esk1, i, arr) => arr.findIndex(esk2 => esk2.eskId === esk1.eskId) === i)

    return newData
  }

  getSidekicks = async () => {
    const sponsorId = (await this.checkUser()).sponsorId
    const response = await this.request<T.GetSidekicksResponse>(
      `sponsor/sponsor_data_by_id/${sponsorId}`,
      'get',
      undefined,
      {
        camelcase: false,
      },
    )
    return this.camelCaseSidekicksResponse(response)
  }

  getSidekickPool = async () => {
    const response = await this.request<T.GetSidekicksResponse>('/sponsor/sidekicks/available', 'get', undefined, {
      camelcase: false,
    })
    return this.camelCaseSidekicksResponse(response)
  }

  // Helper method to convert sidekick response to camelCase while preserving _id fields
  private camelCaseSidekicksResponse(response: any): T.GetSidekicksResponse {
    const camelCaseWithIdPreservation = (obj: any): any => {
      if (Array.isArray(obj)) {
        return obj.map(item => camelCaseWithIdPreservation(item))
      } else if (obj && typeof obj === 'object') {
        const result: any = {}
        Object.keys(obj).forEach(key => {
          if (key === '_id') {
            // Preserve _id field as-is
            result._id = obj[key]
          } else {
            // CamelCase the key and recursively process the value
            const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
            result[camelKey] = camelCaseWithIdPreservation(obj[key])
          }
        })
        return result
      }
      return obj
    }

    return camelCaseWithIdPreservation(response)
  }

  /**
   * Content
   */
  getContent = async (params: T.GetContentParams) => {
    const response = await this.request<T.GetContentResponse>(`/content/get`, 'get', params)

    if (response.contentKey) {
      console.log('[getContent] contentKey is not null, downloading file...')
      try {
        const file = await this.download({ file_path: response.contentKey })
        response.content = file as string
      } catch (error) {
        console.error(`[getContent] Failed to download file for contentKey "${response.contentKey}":`, error)
        response.content = ''
      }
    }

    return response
  }

  getContentMeta = async (params: T.GetContentParams) => {
    return this.request<T.GetContentResponse>(`/content/get`, 'get', params)
  }

  getContentForPreview = async (params: T.GetContentParams) => {
    // Specifically for the special MSK_SWI_CM that has templatized strings that need to be replaced with the actual values
    const fileId = params.file_id

    if (fileId) {
      try {
        // First try to get rendered file content, if that fails, fallback to getContent
        const renderedContent = await this.downloadRendered({ file_id: fileId })
        return {
          content: renderedContent,
          inProcess: false,
        }
      } catch (error) {
        console.error(`[getContentForPreview] Failed to download file for file_id "${fileId}", using fallback`, error)
      }
    }

    return this.getContent(params)
  }

  updateContentText = async (params: T.UpdateContentTextParams) => {
    const { onProgress, ...requestParams } = params

    const response = await this.request<UpdateContentTextResponse>('/content/update_text', 'post', requestParams, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })

    // If there's no processing needed, return immediately
    if (!response.inProcess) {
      return response
    }

    await this.pollForProcessing({
      getStatusFn: () => {
        if ('task_id' in params) {
          return this.getContent({ task_id: params.task_id! })
        } else if ('step_id' in params) {
          return this.getContent({ step_id: params.step_id })
        }
        throw new Error('updateContentText: cannot poll without task_id or step_id')
      },
      logPrefix: 'updateContentText',
      onProgress,
    })

    return response
  }

  /**
   * Resources
   */
  getResources = async (params: T.GetResourcesParams) => {
    const response = await this.request<T.GetResourcesResponse>('/cm/get_resources', 'get', params)

    // Note: A foreach loop would make more sense since we're mutating the response, but it cannot be awaited
    await Promise.all(
      response.map(async resource => {
        if (resource.resourceKey) {
          console.log('[getResources] resourceKey is not null, downloading file...')
          try {
            const file = await this.download({ file_path: resource.resourceKey as string })
            resource.data = file as string
          } catch (error) {
            console.error(`[getResources] Failed to download file for resourceKey "${resource.resourceKey}":`, error)
            resource.data = ''
          }
        }
        return resource
      }),
    )

    // Exclude source files — callers only need non-source (uploaded/linked) resources
    return response.filter(resource => resource.isSourceFile !== true)
  }

  createResource = async (params: T.CreateResourceParams) => {
    const { onProgress, ...requestParams } = params

    const response = await this.request<T.CreateResourceResponse>('/cm/add_resource', 'post', requestParams, {
      headers: { 'Content-Type': 'multipart/form-data' },
      formSerializer: { indexes: null },
    })

    // If there's no processing needed, return immediately
    if (!response.inProcess) {
      return response
    }

    await this.pollForProcessing({
      getStatusFn: async () => {
        const resources = await this.getResources(
          params.task_id ? { task_id: params.task_id } : { step_id: params.step_id! },
        )
        const resource = resources.find(r => r.resourceId === response.resourceId)
        if (!resource) {
          throw new Error('createResource: resource not found in polling response')
        }
        return resource
      },
      logPrefix: 'createResource',
      onProgress,
    })

    return response
  }

  updateResource = async (params: T.UpdateResourceParams) => {
    const { onProgress, ...requestParams } = params

    const response = await this.request<T.UpdateResourceResponse>('/cm/update_resource', 'post', requestParams, {
      headers: { 'Content-Type': 'multipart/form-data' },
      formSerializer: { indexes: null },
    })

    // If there's no processing needed, return immediately
    if (!response.inProcess) {
      return response
    }

    await this.pollForProcessing({
      getStatusFn: async () => {
        const resources = await this.getResources(
          params.task_id ? { task_id: params.task_id } : { step_id: params.step_id! },
        )
        const resource = resources.find(r => r.resourceId === params.resource_id)
        if (!resource) {
          throw new Error('updateResource: resource not found in polling response')
        }
        return resource
      },
      logPrefix: 'updateResource',
      onProgress,
    })

    return response
  }

  deleteResource = async (params: T.DeleteResourceParams) => {
    return this.request<unknown>('/cm/delete_resource', 'post', params)
  }

  /**
   * FAQs
   */
  getFAQs = async (params: T.GetFAQsParams) => {
    return this.request<T.GetFAQsResponse>('/cm/get_faqs', 'get', params)
  }

  createFAQs = async (params: T.CreateFAQsParams) => {
    return this.request<T.StepRepublishingResponse>('/cm/add_faqs', 'post', params)
  }

  updateFAQs = async (params: T.UpdateFAQsParams) => {
    return this.request<T.StepRepublishingResponse>('/cm/update_faqs', 'post', params)
  }

  deleteFAQs = async (params: T.DeleteFAQsParams) => {
    return this.request<T.StepRepublishingResponse>('/cm/delete_faqs', 'post', params)
  }

  /**
   * Steps
   */
  getSteps = async (params: T.GetStepsParams) => {
    return this.request<T.GetStepsResponse>('/step/task_steps', 'get', params)
  }

  getStep = async (params: T.GetStepParams) => {
    return this.request<T.GetStepResponse>('/step/get', 'get', params)
  }

  createStep = async (params: T.CreateStepParams, signal?: AbortSignal, options?: { waitForProcessing?: boolean }) => {
    const { onProgress, ...requestParams } = params

    const response = await this.request<T.CreateStepResponse>('/step/create', 'post', requestParams, {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal,
    })

    // If there's no processing needed or caller opted out of waiting, return immediately
    if (!response.inProcess || options?.waitForProcessing === false) {
      return response
    }

    await this.pollForProcessing({
      getStatusFn: () => this.getContent({ step_id: response.stepId }),
      logPrefix: 'createStep',
      onProgress,
      signal,
      cleanupFn: async () => {
        console.log('[api | createStep] cleanupFn: Deleting step due to abort command')
        await this.deleteStep({ step_id: response.stepId, task_id: params.task_id })
      },
    })

    return response
  }

  deleteStep = async (params: T.DeleteStepParams) => {
    return this.request<unknown>(`/step/delete?step_id=${params.step_id}&task_id=${params.task_id}`, 'delete')
  }

  updateStep = async (params: T.UpdateStepParams) => {
    return this.request<T.UpdateStepResponse>('/step/update', 'post', params)
  }

  reorderSteps = async (params: T.ReorderStepsParams) => {
    return this.request<unknown>('/step/reorder', 'post', params)
  }

  /**
   * Generate
   */
  generateMetadata = async (params: T.GenerateMetadataParams) => {
    return this.request<T.GenerateMetadataResponse>('/cm/generate_metadata', 'post', params)
  }

  generateFAQs = async (params: T.GenerateFAQsParams) => {
    return this.request<T.GenerateFAQsResponse>('/cm/generate_questions', 'post', params)
  }

  generateVideoMetadata = async (params: T.GenerateVideoMetadataParams) => {
    return this.request<T.GenerateVideoMetadataResponse>('/content/generate_video_metadata', 'post', params)
  }

  generateImageMetadata = async (params: T.GenerateImageMetadataParams) => {
    return this.request<T.GenerateImageMetadataResponse>('/content/generate_image_metadata', 'post', params)
  }

  generateDemographic = async (params: T.GenerateDemographicParams) => {
    return this.request<T.GenerateDemographicResponse>('/cm/generate_demographic', 'post', params)
  }

  generateObjectives = async (params: T.GenerateObjectivesParams) => {
    return this.request<T.GenerateObjectivesResponse>('/cm/generate_objectives', 'post', params)
  }

  generateOutline = async (params: T.GenerateOutlineParams) => {
    return this.request<T.GenerateOutlineResponse>('/cm/generate_outline', 'post', params)
  }

  generate = async (params: T.GenerateParams) => {
    return this.request<T.GenerateResponse>('/cm/generate', 'post', params)
  }

  /**
   * Sponsor Topics
   */
  getSponsorTopics = async () => {
    return this.request<T.GetSponsorTopicsResponse>('/sponsor/cm_topics_by_user_id', 'get')
  }

  /**
   * Sponsor Terminology
   */
  getPlaceholderTerminology = async () => {
    return this.request<T.GetCurrentSponsorPlaceholdersResponse>('/sponsor/current_sponsor_placeholders', 'get')
  }

  getCustomerTerminologyPlaceholders = async () => {
    return this.request<T.GetCustomerTerminologyPlaceholderResponse>('/sponsor/current_customer_placeholders', 'get')
  }

  async updateTerminology(params: Partial<T.UpdateTerminologyParams>): Promise<void> {
    const data = {
      id: (await this.checkUser()).sponsorId,
      placeholders_values: {
        sponsor_crisis_instruction: params.sponsorCrisisInstruction,
        sponsor_name_short: params.sponsorNameShort,
        sponsor_name: params.sponsorName,
        user_type_name: params.userTypeName,
        member_introduction: params.memberIntroduction,
        sme_introduction: params.smeIntroduction,
        sponsor_admin_introduction: params.sponsorAdminIntroduction,
        // TODO: Disabling these for now - while the TerminologyPanel only shows the required terminology
        // expert_sidekick: params.expertSidekick,
        // road_to_success: params.roadToSuccess,
        // road_to_success_short: params.roadToSuccessShort,
        // road_to_success_item: params.roadToSuccessItem,
        // member_sidekick_type: params.memberSidekickType,
        // step: params.step,
        // frequently_asked_question: params.frequentlyAskedQuestion,
        // faq_term: params.faqTerm,
        // sme_term: params.smeTerm,
        // subject_matter_expert: params.subjectMatterExpert,
        // sponsor_specific_prompt: params.sponsorSpecificPrompt, // TODO: Currently get server error for having this be a blank string, ... even though that's the default value
      },
    }

    console.log('updateTerminology', data)

    await this.request('/sponsor/update', 'post', data)
  }

  /**
   * Channel Brand
   *
   * Brand fields (brand_image, brand_title, brand_subtitle) are returned as
   * part of /sponsor/current (getSponsorInfo). No separate fetch needed.
   *
   * Sends brand fields and terminology placeholders in a single request to
   * /sponsor/update. brand_title is the canonical "channel name"; the legacy
   * sponsorName placeholder is kept in sync for backward compat.
   */

  async saveBranding(params: T.SaveBrandingParams): Promise<void> {
    const t = params.terminology
    await this.request('/sponsor/update', 'post', {
      id: (await this.checkUser()).sponsorId,
      ...params.brand,
      placeholders_values: {
        sponsor_crisis_instruction: t.sponsorCrisisInstruction,
        sponsor_name_short: t.sponsorNameShort,
        sponsor_name: t.sponsorName,
        user_type_name: t.userTypeName,
        member_introduction: t.memberIntroduction,
        sme_introduction: t.smeIntroduction,
        sponsor_admin_introduction: t.sponsorAdminIntroduction,
      },
    })
  }

  /**
   * Analytics
   */

  /**
   * Get the number of SME logins by date over a specific date range
   * @param params - GetSmeLoginsByDateParams - provide a date range
   * @returns SmeLoginsByDateResponse
   */
  getSmeLoginsByDate = async (params: T.GetSmeLoginsByDateParams): Promise<T.SmeLoginsByDateResponse> => {
    return await this.request<T.SmeLoginsByDateResponse>('/analytics/sme_logins_by_date', 'get', params)
  }

  getCmEditsByDate = async (params: T.GetCmEditsByDateParams): Promise<T.CmEditsByDateResponse> => {
    return await this.request<T.CmEditsByDateResponse>('/analytics/cm_edits_by_date', 'get', params)
  }

  getEditsPerCm = async (params: T.GetEditsPerCmParams): Promise<T.EditsPerCmResponse> => {
    return await this.request<T.EditsPerCmResponse>('/analytics/edits_per_cm', 'get', params)
  }

  /**
   * Get conversation time series data by specific to a content module over a date range
   * @param params - GetCmConversationsByDateParams - provide a content module id and date range
   * @returns CmConversationByDateResponse
   */
  getCmConversationsByDate = async (
    params: T.GetCmConversationsByDateParams,
  ): Promise<T.CmConversationByDateResponse> => {
    return await this.request<T.CmConversationByDateResponse>('/analytics/cm_conversations_by_date', 'get', params)
  }

  /**
   * Get conversation line chart data by specific to a topic
   * @param topic - Topic type
   * @returns ConversationLineChartData
   */
  getTopicConversationsByDate = async (
    params: T.GetTopicConversationsByDateParams,
  ): Promise<T.TopicConversationByDateResponse> => {
    return await this.request<T.TopicConversationByDateResponse>(
      '/analytics/topic_conversations_by_date',
      'get',
      params,
    )
  }

  copyContentModule = async (params: T.CopyContentModuleParams) => {
    return await this.request<T.CopyContentModuleResponse>('/cm/copy', 'post', params)
  }

  /**
   * Get active users line chart data
   * @returns ActiveMembersByDateResponse
   */
  getActiveMembersByDate = async (params: T.GetActiveMembersByDateParams): Promise<T.ActiveMembersByDateResponse> => {
    return await this.request<T.ActiveMembersByDateResponse>('/analytics/active_members_by_date', 'get', params)
  }

  getAllConversationsPerCm = async (
    params: T.AllConversationsPerCmParams,
  ): Promise<T.AllConversationsPerCmResponse> => {
    return await this.request<T.AllConversationsPerCmResponse>('/analytics/all_conversations_per_cm', 'get', params)
  }

  /**
   * Generic parameter serializer that handles arrays without brackets
   * Some backend endpoints expect array parameters as multiple query params with same key
   * (e.g., tags=a&tags=b) rather than the standard format with brackets (e.g., tags[]=a&tags[]=b)
   *
   * @returns AxiosRequestConfig paramsSerializer for endpoints that need this format
   */
  private createArrayParamsSerializer() {
    return {
      serialize: (params: any) => {
        const searchParams = new URLSearchParams()

        Object.entries(params).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            // For arrays, add multiple parameters with the same key (no brackets)
            // This format is required by certain backend endpoints
            value.forEach(item => {
              searchParams.append(key, String(item))
            })
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value))
          }
        })

        return searchParams.toString()
      },
    }
  }

  /**
   * Transform feedback response from snake_case to camelCase while preserving specific fields
   * Backend inconsistently returns some fields that get corrupted by standard camelCase transformation:
   * - Content module titles may contain special characters that break camelCase conversion
   * - Topic IDs contain hyphens that get removed by camelCase transformation
   */
  private transformFeedbackResponse(rawResponse: any): T.GetFeedbackResponse {
    // Apply standard camelCase transformation
    const response = camelCase(rawResponse, 999) as any

    // Preserve content module titles in their original format
    // Backend returns these with special characters/formatting that must be maintained
    if (rawResponse.stats?.by_content_module) {
      response.stats.byContentModule = rawResponse.stats.by_content_module
    }

    // Preserve topic IDs in their original format
    // Backend returns topic IDs with hyphens that get removed by camelCase transformation
    if (rawResponse.stats?.by_topic) {
      response.stats.byTopic = rawResponse.stats.by_topic
    }

    return response as T.GetFeedbackResponse
  }

  /**
   * Get sponsor-specific feedback with filtering, sorting, and pagination
   * This endpoint automatically filters to the current sponsor's feedback only
   *
   * Note: This method requires special handling due to backend API quirks:
   * - Array parameters must be serialized without brackets
   * - Response contains fields that break standard camelCase transformation
   *
   * @param params - GetFeedbackParams - pagination and filtering options
   * @returns GetFeedbackResponse - paginated list of feedback annotations
   */
  getFeedback = async (params: T.GetFeedbackParams = {}): Promise<T.GetFeedbackResponse> => {
    const requestParams = {
      page: params.page || 1,
      page_size: params.pageSize || 10,
      ...(params.startDate && { start_date: params.startDate }),
      ...(params.endDate && { end_date: params.endDate }),
      ...(params.topicIds && { topic_ids: params.topicIds }),
      ...(params.contentModuleIds && { content_module_ids: params.contentModuleIds }),
      ...(params.tags && { tags: params.tags }),
      ...(params.feedbackType && { feedback_type: params.feedbackType }),
      ...(params.comments && { comments: params.comments }),
      ...(params.sortBy && { sort_by: params.sortBy }),
      ...(params.sortOrder && { sort_order: params.sortOrder }),
    }

    // Get raw response without camelCase transformation to preserve special fields
    const rawResponse = await this.request<any>('/annotations/get_sponsor_feedback', 'get', requestParams, {
      camelcase: false,
      paramsSerializer: this.createArrayParamsSerializer(),
    })

    return this.transformFeedbackResponse(rawResponse)
  }

  /**
   * Get list of available tags for feedback annotations
   * @param params - GetFeedbackTagListParams - currently no parameters needed
   * @returns GetFeedbackTagListResponse - list of available tags
   */
  getFeedbackTagList = async (params: T.GetFeedbackTagListParams = {}): Promise<T.GetFeedbackTagListResponse> => {
    return await this.request<T.GetFeedbackTagListResponse>('/annotations/get-annotation-tags', 'get', params)
  }

  private async pollForProcessing<T extends ProcessProgress>({
    getStatusFn,
    logPrefix,
    onProgress,
    signal,
    cleanupFn,
  }: {
    getStatusFn: () => Promise<T>
    logPrefix: string
    onProgress?: (progress: T['processProgress']) => void
    signal?: AbortSignal
    cleanupFn?: () => Promise<void>
  }): Promise<T | null> {
    console.log(`${logPrefix}: start polling`)
    let pollResponse: T | null = null
    let timeout = 30 * 60 * 1000 // 30 minute timeout

    do {
      // Check if the operation was aborted
      if (signal?.aborted) {
        console.log(`${logPrefix}: polling aborted, running cleanup`)
        if (cleanupFn) {
          try {
            await cleanupFn()
          } catch (error) {
            console.error(`${logPrefix}: cleanup failed`, error)
          }
        }
        throw new Error(`${logPrefix}: Operation was cancelled`)
      }

      // wait for 5s between polling
      await new Promise(resolve => setTimeout(resolve, 5000))
      timeout -= 5000
      console.log(`${logPrefix}: poll`)

      pollResponse = await getStatusFn()

      if (pollResponse.processProgress?.error) {
        const msg = pollResponse.processProgress.error.message || 'Failed to process file'
        console.error(`${logPrefix}: processing error - ${msg}`)
        throw new FileProcessingError(msg)
      }

      if (onProgress && pollResponse.processProgress) {
        // FIXME: need to compensate for backend sending null instead of completion
        // values when finished processing
        const { total, current } = pollResponse.processProgress
        if (total === null || current === null) {
          pollResponse.processProgress.total = 1
          pollResponse.processProgress.current = 1
        }
        onProgress(pollResponse.processProgress)
      }

      if (timeout <= 0) {
        console.log(`${logPrefix}: polling timed out`)
        break
      }
    } while (this.isProcessing(pollResponse))

    console.log(`${logPrefix}: done polling`)
    return pollResponse
  }

  async updateTopics(params: { topics: T.TopicResponse[]; esks?: T.ESKResponse[] }): Promise<void> {
    const requestBody: any = {
      id: (await this.checkUser()).sponsorId,
      sponsorName: (await this.checkUser()).sponsorName,
      topics: params.topics.map(topic => this.formatTopicRequest(topic)),
    }

    // Include ESKs if provided
    if (params.esks && params.esks.length > 0) {
      requestBody.esk_types = params.esks.map(esk => this.formatEskRequest(esk))
    }

    await this.request('/sponsor/update', 'post', requestBody)
  }

  formatTopicRequest = (topic: T.TopicResponse): T.TopicRequest => {
    return {
      id: topic.id,
      key: topic.key,
      displayName: topic.displayName,
      subtopics: topic.subtopics,
      priority: topic.priority,
      sk_id: topic.skId,
      not_removable: topic.notRemovable,
    }
  }

  formatEskRequest = (esk: T.ESKResponse): T.ESKRequest => {
    return {
      type: esk.type,
      name: esk.name,
      introduction: esk.introduction ?? ' ',
      system_prompt: esk.systemPrompt ?? ' ',
      allowed_for_switch_cm: esk.allowedForSwitchCm,
      tts_id: esk.ttsId,
      topic_id: esk.topicId ?? '',
      active: esk.active,
      created_at: esk.createdAt,
      additional_information: esk.additionalInformation ?? ' ',
      avatar_id: esk.avatarId,
      category: esk.category ?? null,
      created_by: esk.createdBy,
      expertise: esk.expertise,
      gender: esk.gender,
      avatar_image: esk.avatarImage,
      avatar_rive_file: esk.avatarRiveFile,
      voice_speed: esk.voiceSpeed,
      pitch: esk.pitch,
      not_removable: esk.notRemovable,
      sponsor_id: esk.sponsorId,
    }
  }

  formatMskRequest = (msk: T.MSKResponse): T.MSKRequest => {
    // Only include id and _id if they are defined, important that they are omitted for new sidekicks
    return {
      ...(msk.id !== undefined && { id: msk.id }),
      ...(msk._id !== undefined && { _id: msk._id }),
      default: msk.default,
      name: msk.name,
      tts_id: msk.ttsId,
      created_by: msk.createdBy,
      sponsor_id: msk.sponsorId,
      active: msk.active,
      created_at: msk.createdAt,
      avatar_id: msk.avatarId,
      avatar_image: msk.avatarImage,
      avatar_rive_file: msk.avatarRiveFile,
      pitch: msk.pitch,
      voice_speed: msk.voiceSpeed,
      gender: msk.gender,
      isRequired: msk.isRequired,
    }
  }

  async updateEsks(params: T.UpdateEskParams): Promise<void> {
    const formattedEsks = params.esks.map(esk => this.formatEskRequest(esk))

    return await this.request('/sponsor/update', 'post', {
      id: (await this.checkUser()).sponsorId,
      esk_types: formattedEsks,
      topics: params.topics,
    })
  }

  async updateMsks(params: T.UpdateMskParams): Promise<void> {
    const formattedMsks = params.msks.map(msk => this.formatMskRequest(msk))

    return await this.request('/sponsor/update', 'post', {
      id: (await this.checkUser()).sponsorId,
      msk_types: formattedMsks,
    })
  }

  async updateSponsorSetupProgress(params: {
    setup_progress: T.SetupProgress
  }): Promise<T.UpdateSponsorSetupProgressResponse> {
    return await this.request('/sponsor/update', 'post', {
      id: (await this.checkUser()).sponsorId,
      setup_progress: params.setup_progress,
    })
  }

  async updateTopicsEnabled(topicsEnabled: boolean): Promise<void> {
    return await this.request('/sponsor/update', 'post', {
      id: (await this.checkUser()).sponsorId,
      topics_enabled: topicsEnabled,
    })
  }

  async updateSubscriptionUrl(subscriptionUrl: string | null): Promise<void> {
    return await this.request('/sponsor/update', 'post', {
      id: (await this.checkUser()).sponsorId,
      subscription_url: subscriptionUrl,
    })
  }

  async updateGuestUserMessage(guestUserMessage: string | null): Promise<void> {
    return await this.request('/sponsor/update', 'post', {
      id: (await this.checkUser()).sponsorId,
      guest_user_message: guestUserMessage,
    })
  }

  async getSponsorFactFields(): Promise<T.SponsorFactFieldRecord[]> {
    return await this.request('/facts/sponsor_facts', 'get')
  }

  async updateSponsorFactFields(factFields: T.FactField[], sponsorId: string): Promise<void> {
    // FIXME: Although fact_order needs to be changed, as its the sort key for the member app, this endpoint actually
    // respects the data order instead. It's unclear how the fact_order in the API call actually affects the stored data.
    const sortedFactFields = factFields.sort((a, b) => a.factOrder - b.factOrder)

    // For each fact field we create a valid update record for the endpoint
    const apiUpdateRecords = sortedFactFields.map(field => {
      // Extract only the API valid fields
      const apiUpdateRecord: T.SponsorFactFieldUpdateRecord = {
        fact_order: field.factOrder,
        fact_type: field.factType,
        name: field.name,
        field_type: field.fieldType,
        header_id: field.headerId,
        active: field.enabled,
      }

      return apiUpdateRecord
    })

    return await this.request('/facts/update_sponsor_facts', 'post', {
      facts: apiUpdateRecords,
      // Yes, this is actually camelcase unlike the fields themselves
      sponsorId: sponsorId,
    })
  }

  async getAllFactFields(): Promise<T.FactFieldRecord[]> {
    return await this.request('/facts/fact_fields', 'get')
  }

  // User Management
  // ------------------------------------------------------------
  getUsers = async (params: T.GetUsersParams): Promise<T.GetUsersResponse> => {
    const baseParams = {
      page: params.page,
      page_size: params.pageSize,
      sort_by: params.sortBy,
      sort_dir: params.sortDir,
      filter_by: params.filterBy,
      ...(params.isUserDisabled !== undefined && { is_user_disabled: params.isUserDisabled }),
      // Only include role-based parameters if they are provided
      ...(params.role === 'SWI Admin' && { swi_admin: true }),
      ...(params.role === 'Admin' && {
        swi_admin: false,
        sponsor_admin: true,
      }),
      ...(params.role === 'SME' && {
        swi_admin: false,
        sponsor_admin: false,
        sme: true,
      }),
      ...(params.role === 'User' && {
        swi_admin: false,
        sponsor_admin: false,
        sme: false,
      }),
    }

    return await this.request<T.GetUsersResponse>('/members/get', 'get', baseParams)
  }

  createUser = async (params: T.CreateUserParams): Promise<T.CreateUserResponse> => {
    const response = await this.request<T.CreateUserResponse>('/members/create', 'post', params)
    const success = 'message' in response && response.message === 'User created successfully.'

    return {
      success,
      message: response.message,
    }
  }

  createBulkUsers = async (params: T.CreateBulkUsersParams): Promise<T.CreateBulkUsersResponse> => {
    return await this.request<T.CreateBulkUsersResponse>('/members/bulk_upload', 'post', params, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  changeUserStatus = async (params: T.ChangeUserStatusParams): Promise<T.ChangeUserStatusResponse> => {
    const response = await this.request<T.ChangeUserStatusResponse>('/members/change_user_status', 'post', params)
    const success = 'message' in response && response.message === 'User status updated successfully.'

    return {
      success,
      message: response.message,
    }
  }

  updateUser = async (params: T.UpdateUserParams): Promise<T.UpdateUserResponse> => {
    const response = await this.request<T.UpdateUserResponse>('/members/update', 'post', params)
    const success = 'message' in response && response.message === 'User updated successfully.'
    return {
      success,
      message: response.message,
    }
  }

  reinviteUser = async (params: T.ReinviteUserParams): Promise<T.ReinviteUserResponse> => {
    const response = await this.request<T.ReinviteUserResponse>('/members/re_invite', 'post', params)
    const success = 'message' in response && typeof response.message === 'string'
    return {
      success,
      message: response.message,
    }
  }

  previewInvite = async (params: T.PreviewInviteParams): Promise<T.PreviewInviteResponse> => {
    return await this.request<T.PreviewInviteResponse>('/members/preview_invite', 'post', params)
  }

  /**
   * Presentation Personalities
   */
  getPresentationPersonalities = async (): Promise<T.GetPresentationPersonalitiesResponse> => {
    return await this.request<T.GetPresentationPersonalitiesResponse>('/presentation/personalities', 'get')
  }

  /**
   * Presentation Styles
   */
  getPresentationStyles = async (): Promise<T.GetPresentationStylesResponse> => {
    return await this.request<T.GetPresentationStylesResponse>('/presentation/styles', 'get')
  }

  // Billing
  // ------------------------------------------------------------
  getBillingInfo = async (): Promise<T.GetBillingInfoResponse> => {
    return await this.request<T.GetBillingInfoResponse>('/billing/billing_info', 'get')
  }

  getBillingCreditsData = async (params: T.GetBillingCreditsDataParams): Promise<T.GetBillingCreditsDataResponse> => {
    return await this.request<T.GetBillingCreditsDataResponse>('/billing/credits_data', 'get', params)
  }

  getSubscribersData = async (params?: T.GetSubscribersDataParams): Promise<T.GetSubscribersDataResponse> => {
    return await this.request<T.GetSubscribersDataResponse>('/billing/subscribers_data', 'get', params)
  }

  // Purchase credits one time
  purchaseCreditsWithPaymentMethod = async (params: T.PurchaseCreditsParams): Promise<T.PurchaseCreditsResponse> => {
    return await this.request<T.PurchaseCreditsResponse>('/billing/generate_client_secret', 'post', {
      ...params,
      attempt_stored_payment_purchase: true,
    })
  }

  purchaseCreditsNoPaymentMethod = async (params: T.PurchaseCreditsParams): Promise<T.PurchaseCreditsResponse> => {
    return await this.request<T.PurchaseCreditsResponse>('/billing/generate_client_secret', 'post', {
      ...params,
      attempt_stored_payment_purchase: false,
    })
  }

  // Modify auto refill settings
  updateAutoRefill = async (params: T.EnableAutoRefillParams): Promise<T.EnableAutoRefillResponse> => {
    return await this.request<T.EnableAutoRefillResponse>('/billing/auto_refill_status', 'post', {
      ...params,
      auto_refill_status: true,
    })
  }

  disableAutoRefill = async (_params: T.DisableAutoRefillParams = {}): Promise<T.DisableAutoRefillResponse> => {
    return await this.request<T.DisableAutoRefillResponse>('/billing/auto_refill_status', 'post', {
      auto_refill_status: false,
      bundle_id: '',
      threshold: null,
    })
  }

  changePaymentMethod = async (): Promise<T.ChangePaymentMethodResponse> => {
    return await this.request<T.ChangePaymentMethodResponse>('/billing/generate_setup_intent_secret', 'post', {})
  }

  // Removing payment method (equivalent to disabling auto refill)
  removePaymentMethod = async (params: T.RemovePaymentMethodParams): Promise<T.RemovePaymentMethodResponse> => {
    return await this.request<T.RemovePaymentMethodResponse>('/billing/clear_payment_methods', 'post', params)
  }

  changeToPaidChannel = async (): Promise<T.ChangeToPaidChannelResponse> => {
    return await this.request<T.ChangeToPaidChannelResponse>('/billing/change_to_paid_channel', 'post')
  }

  getInvoices = async (params: T.GetInvoicesParams = {}): Promise<T.GetInvoicesResponse> => {
    return await this.request<T.GetInvoicesResponse>('/billing/invoices', 'get', params)
  }

  // Onboarding
  // ------------------------------------------------------------
  createCustomerInfo = async (params: T.CreateCustomerInfoParams): Promise<T.CreateCustomerInfoResponse> => {
    return await this.authRequest<T.CreateCustomerInfoResponse>('/onboarding/create_customer_info', 'post', params)
  }

  sendConfirmationEmail = async (params: T.SendConfirmationEmailParams): Promise<T.SendConfirmationEmailResponse> => {
    return await this.authRequest<T.SendConfirmationEmailResponse>(
      '/onboarding/send_confirmation_email',
      'post',
      params,
    )
  }

  verifyEmail = async (params: T.VerifyEmailParams): Promise<T.VerifyEmailResponse> => {
    return await this.authRequest<T.VerifyEmailResponse>('/onboarding/validate_customer_email', 'post', params)
  }

  resendVerificationEmail = async (
    params: T.ResendVerificationEmailParams,
  ): Promise<T.ResendVerificationEmailResponse> => {
    return await this.authRequest<T.ResendVerificationEmailResponse>(
      '/onboarding/resend_verification_email',
      'post',
      params,
    )
  }

  getPlans = async (): Promise<T.Plan[]> => {
    const response = await this.authRequest<T.GetPlansResponse>('/bundles/bundles.json', 'get')
    return response.plans
  }

  generateClientSecret = async (params: T.GenerateClientSecretParams): Promise<T.GenerateClientSecretResponse> => {
    return await this.authRequest<T.GenerateClientSecretResponse>('/onboarding/generate_client_secret', 'post', params)
  }

  createCustomer = async (params: T.CreateCustomerParams): Promise<T.CreateCustomerResponse> => {
    return await this.authRequest<T.CreateCustomerResponse>('/onboarding/create_customer', 'post', params)
  }

  /**
   * General Information Content
   */
  listGeneralInfo = async (params?: T.ListGeneralInfoParams) => {
    return this.request<T.GeneralInfoContent[]>('/general-info/list', 'get', params)
  }

  getGeneralInfo = async (contentId: string) => {
    const response = await this.request<T.GeneralInfoContent>(`/general-info/${contentId}`, 'get')

    if (response.contentKey) {
      try {
        const file = await this.download({ file_path: response.contentKey })
        response.content = file as string
      } catch (error) {
        console.error(`[getGeneralInfo] Failed to download file for contentKey "${response.contentKey}":`, error)
        response.content = ''
      }
    }

    return response
  }

  createGeneralInfo = async (params: T.CreateGeneralInfoParams) => {
    return this.request<T.CreateGeneralInfoResponse>('/general-info/create', 'post', params, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  updateGeneralInfo = async (contentId: string, params: T.UpdateGeneralInfoParams) => {
    return this.request<T.UpdateGeneralInfoResponse>(`/general-info/${contentId}/update`, 'post', params, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  deleteGeneralInfo = async (contentId: string) => {
    return this.request<T.DeleteGeneralInfoResponse>(`/general-info/${contentId}`, 'delete')
  }

  // Private Methods
  // ------------------------------------------------------------
  private isProcessing(response: any): boolean {
    return response?.inProcess === true
  }
}
